/*!
 * Parenting Optimize - Decision Engine
 * File: parenting-engine.js
 * Version: 1.0.0
 *
 * Purpose:
 * Converts parent profile + child profile + family profile + situation + history
 * into a simple parent-friendly plan.
 *
 * This file should not handle UI rendering.
 * This file should not contain large content libraries.
 * Full knowledge/content belongs in parenting-knowledge.js.
 */

(function (window) {
  "use strict";

  const PO = (window.PO = window.PO || {});

  // ==========================================================================
  // 01. ENGINE NAMESPACE
  // ==========================================================================

  PO.Engine = {
    version: "1.0.0",

    generatePlan(context = {}) {
      const normalized = this.normalizeContext(context);
      const scores = this.scoreAll(normalized);
      const insight = this.identifyPrimaryPattern(normalized, scores);
      const plan = this.buildPlan(normalized, scores, insight);

      return plan;
    },

    normalizeContext(context = {}) {
      return {
        child: context.child || {},
        parent: context.parent || {},
        family: context.family || {},
        situation: context.situation || "general",
        answers: context.answers || {},
        history: Array.isArray(context.history) ? context.history : [],
        feedback: Array.isArray(context.feedback) ? context.feedback : [],
        timestamp: new Date().toISOString()
      };
    },

    scoreAll(ctx) {
      return {
        parent: this.scoreParent(ctx),
        child: this.scoreChild(ctx),
        family: this.scoreFamily(ctx),
        situation: this.scoreSituation(ctx),
        learning: this.scoreLearning(ctx)
      };
    },

    // ==========================================================================
    // 02. PARENT ENGINE
    // Hidden inspiration: natural stress response model / temperament logic.
    // Customer never sees methodology names.
    // ==========================================================================

    scoreParent(ctx) {
      const response = ctx.parent.responseStyle || ctx.answers.parentResponse || "unknown";

      const profile = {
        directiveness: 0,
        explanation: 0,
        repetition: 0,
        rescue: 0,
        variability: 0,
        emotionalLoad: 0,
        likelyPattern: "balanced"
      };

      if (response === "firm") {
        profile.directiveness += 3;
        profile.emotionalLoad += 1;
        profile.likelyPattern = "directive";
      }

      if (response === "explain") {
        profile.explanation += 3;
        profile.variability += 1;
        profile.likelyPattern = "explainer";
      }

      if (response === "repeat") {
        profile.repetition += 3;
        profile.emotionalLoad += 2;
        profile.likelyPattern = "repeater";
      }

      if (response === "give-in") {
        profile.rescue += 3;
        profile.variability += 2;
        profile.likelyPattern = "rescuer";
      }

      if (response === "variable") {
        profile.variability += 3;
        profile.emotionalLoad += 1;
        profile.likelyPattern = "inconsistent";
      }

      return profile;
    },

    // ==========================================================================
    // 03. CHILD ENGINE
    // Inspired by child temperament research: persistence, adaptability,
    // transition difficulty, sensitivity, intensity.
    // ==========================================================================

    scoreChild(ctx) {
      const child = ctx.child || {};
      const strengths = Array.isArray(child.strengths) ? child.strengths : [];
      const challenge = child.mainChallenge || ctx.situation || "general";

      const profile = {
        persistence: 1,
        transitionNeed: 1,
        sensitivity: 1,
        autonomyNeed: 1,
        activityLevel: 1,
        contributionStyle: "general",
        likelyNeed: "clarity"
      };

      if (strengths.includes("strong-willed")) {
        profile.persistence += 3;
        profile.autonomyNeed += 2;
        profile.likelyNeed = "controlled-choice";
      }

      if (strengths.includes("sensitive")) {
        profile.sensitivity += 3;
        profile.likelyNeed = "connection-before-correction";
      }

      if (strengths.includes("creative")) {
        profile.autonomyNeed += 2;
        profile.contributionStyle = "creative";
      }

      if (strengths.includes("technical")) {
        profile.persistence += 1;
        profile.contributionStyle = "builder";
      }

      if (strengths.includes("helpful")) {
        profile.contributionStyle = "helper";
      }

      if (strengths.includes("organized")) {
        profile.transitionNeed += 1;
        profile.contributionStyle = "organizer";
      }

      if (strengths.includes("energetic")) {
        profile.activityLevel += 3;
      }

      if (challenge === "bedtime" || challenge === "morning") {
        profile.transitionNeed += 2;
      }

      if (challenge === "screens") {
        profile.persistence += 2;
        profile.autonomyNeed += 1;
      }

      return profile;
    },

    // ==========================================================================
    // 04. FAMILY ENGINE
    // Inspired by family systems: alignment, consistency, structure, stress.
    // ==========================================================================

    scoreFamily(ctx) {
      const structure = ctx.family.structure || ctx.answers.familyStructure || "unknown";

      const profile = {
        structure,
        alignmentRisk: 0,
        consistencyNeed: 1,
        parentLoad: 1,
        likelySystemPattern: "early-profile"
      };

      if (structure === "single") {
        profile.parentLoad += 2;
        profile.consistencyNeed += 1;
        profile.likelySystemPattern = "single-parent-load";
      }

      if (structure === "two-together") {
        profile.alignmentRisk += 1;
        profile.likelySystemPattern = "two-parent-alignment";
      }

      if (structure === "shared") {
        profile.alignmentRisk += 3;
        profile.consistencyNeed += 3;
        profile.likelySystemPattern = "split-context";
      }

      if (structure === "other") {
        profile.alignmentRisk += 2;
        profile.consistencyNeed += 2;
        profile.likelySystemPattern = "complex-family-setup";
      }

      return profile;
    },

    // ==========================================================================
    // 05. SITUATION ENGINE
    // Situation-specific priorities.
    // ==========================================================================

    scoreSituation(ctx) {
      const situation = ctx.situation || "general";
      const intensity = ctx.answers.situationIntensity || "moderate";

      const profile = {
        situation,
        intensity,
        requiresCalmingFirst: false,
        requiresTransitionSupport: false,
        requiresBoundary: false,
        requiresConnection: false,
        requiresSkillBuilding: false
      };

      if (["meltdown", "arguing"].includes(situation)) {
        profile.requiresCalmingFirst = true;
        profile.requiresConnection = true;
      }

      if (["bedtime", "morning", "homework"].includes(situation)) {
        profile.requiresTransitionSupport = true;
      }

      if (["screens", "not-listening", "arguing"].includes(situation)) {
        profile.requiresBoundary = true;
      }

      if (["homework", "siblings", "meltdown"].includes(situation)) {
        profile.requiresSkillBuilding = true;
      }

      if (intensity === "urgent" || intensity === "high") {
        profile.requiresCalmingFirst = true;
      }

      return profile;
    },

    // ==========================================================================
    // 06. LEARNING ENGINE
    // Uses prior feedback. V1 is simple; v2 becomes adaptive.
    // ==========================================================================

    scoreLearning(ctx) {
      const feedback = ctx.feedback || [];

      const profile = {
        totalFeedback: feedback.length,
        workedCount: feedback.filter((f) => f.result === "worked").length,
        somewhatCount: feedback.filter((f) => f.result === "somewhat").length,
        failedCount: feedback.filter((f) => ["did-not-work", "worse"].includes(f.result)).length,
        confidence: "low",
        note: "Not enough feedback yet."
      };

      if (profile.totalFeedback >= 3) profile.confidence = "medium";
      if (profile.totalFeedback >= 8) profile.confidence = "high";

      if (profile.workedCount > profile.failedCount) {
        profile.note = "Some previous strategies appear to be helping.";
      }

      if (profile.failedCount > profile.workedCount) {
        profile.note = "We may need to simplify or change the approach.";
      }

      return profile;
    },

    // ==========================================================================
    // 07. PATTERN IDENTIFICATION
    // ==========================================================================

    identifyPrimaryPattern(ctx, scores) {
      const parent = scores.parent;
      const child = scores.child;
      const family = scores.family;
      const situation = scores.situation;

      if (situation.requiresCalmingFirst) {
        return {
          key: "calm-before-teach",
          title: "Calm First Pattern",
          summary: "This moment may need regulation before instruction."
        };
      }

      if (parent.repetition >= 3 && child.persistence >= 3) {
        return {
          key: "repeat-resist-loop",
          title: "Repeat–Resist Loop",
          summary: "More reminders may be increasing resistance."
        };
      }

      if (parent.explanation >= 3 && child.autonomyNeed >= 3) {
        return {
          key: "debate-loop",
          title: "Debate Loop",
          summary: "Too much explanation may be turning instructions into negotiation."
        };
      }

      if (parent.rescue >= 3) {
        return {
          key: "boundary-collapse",
          title: "Soft Boundary Pattern",
          summary: "The child may be learning that distress changes the rule."
        };
      }

      if (family.alignmentRisk >= 3) {
        return {
          key: "split-signal",
          title: "Split-Signal Pattern",
          summary: "The child may be receiving different messages in different contexts."
        };
      }

      if (situation.requiresTransitionSupport || child.transitionNeed >= 3) {
        return {
          key: "transition-friction",
          title: "Transition Friction",
          summary: "The hard part may be changing activities, not the task itself."
        };
      }

      return {
        key: "clarity-consistency",
        title: "Clarity + Consistency",
        summary: "A simple instruction and calm follow-through is the best first move."
      };
    },

    // ==========================================================================
    // 08. PLAN BUILDER
    // ==========================================================================

    buildPlan(ctx, scores, insight) {
      const situationLabel = this.labelSituation(ctx.situation);
      const steps = this.buildSteps(ctx, scores, insight);
      const avoid = this.buildAvoidList(ctx, scores, insight);
      const why = this.buildWhy(ctx, scores, insight);
      const contribution = this.buildContributionIdea(ctx, scores);

      return {
        id: this.uid("plan"),
        version: this.version,
        createdAt: new Date().toISOString(),
        situation: ctx.situation,
        situationLabel,
        insightKey: insight.key,
        title: `${situationLabel}: ${insight.title}`,
        subtitle: "A short plan based on your child, your style, and this situation.",
        estimatedTime: scores.situation.requiresCalmingFirst ? "5 minutes" : "3 minutes",
        steps,
        why,
        avoid,
        contribution,
        learningNote: scores.learning.note,
        confidence: scores.learning.confidence
      };
    },

    buildSteps(ctx, scores, insight) {
      const situation = ctx.situation;
      const parent = scores.parent;
      const child = scores.child;
      const family = scores.family;
      const situationScore = scores.situation;

      if (situationScore.requiresCalmingFirst) {
        return [
          "Lower the intensity first. Use a short sentence like, “I’m here. We’re going to slow this down.”",
          "Do not teach or lecture during the peak of the emotion.",
          "Once the child is calmer, give one clear next step."
        ];
      }

      if (insight.key === "repeat-resist-loop") {
        return [
          "Say the instruction once in a calm, clear sentence.",
          "Offer two acceptable choices so your child has some control.",
          "Follow through after one reminder instead of repeating several times."
        ];
      }

      if (insight.key === "debate-loop") {
        return [
          "Use one sentence. Avoid explaining the whole reason right now.",
          "Say, “You can choose A or B. Both are okay with me.”",
          "If your child argues, calmly repeat the choice once and stop debating."
        ];
      }

      if (insight.key === "boundary-collapse") {
        return [
          "Start with warmth, then state the boundary clearly.",
          "Expect some protest without changing the rule immediately.",
          "Praise the first small move toward cooperation."
        ];
      }

      if (insight.key === "split-signal") {
        return [
          "Before addressing the child, decide the rule clearly with the other adult if possible.",
          "Use the same short wording each time.",
          "Avoid correcting the other parent in front of the child."
        ];
      }

      if (insight.key === "transition-friction") {
        return [
          "Give a short warning before the change: “In five minutes, we’re moving to the next thing.”",
          "Use a simple visual or countdown if possible.",
          "Make the first step tiny: “Just stand up and come to the doorway.”"
        ];
      }

      if (situation === "screens") {
        return [
          "State the screen rule before the device is turned on next time.",
          "Give one countdown warning before time ends.",
          "When time is up, follow through calmly without renegotiating."
        ];
      }

      if (situation === "homework") {
        return [
          "Start with the smallest possible task, not the whole assignment.",
          "Sit nearby for the first two minutes, then step back.",
          "Praise effort before results."
        ];
      }

      if (situation === "siblings") {
        return [
          "Separate the children briefly before deciding what happened.",
          "Ask each child what they needed or wanted.",
          "Give each child one repair action."
        ];
      }

      return [
        "Say the next step calmly and clearly.",
        "Make the choice simple.",
        "Follow through without adding a lecture."
      ];
    },

    buildAvoidList(ctx, scores, insight) {
      const avoid = [];

      if (scores.parent.repetition >= 3) {
        avoid.push("Avoid repeating the same instruction many times.");
      }

      if (scores.parent.explanation >= 3) {
        avoid.push("Avoid turning the moment into a long explanation.");
      }

      if (scores.parent.directiveness >= 3) {
        avoid.push("Avoid increasing pressure too quickly unless safety is involved.");
      }

      if (scores.parent.rescue >= 3) {
        avoid.push("Avoid changing the rule just because your child is upset.");
      }

      if (scores.situation.requiresCalmingFirst) {
        avoid.push("Avoid teaching while your child is emotionally flooded.");
      }

      if (scores.family.alignmentRisk >= 2) {
        avoid.push("Avoid letting the child receive two different answers from two adults.");
      }

      if (!avoid.length) {
        avoid.push("Avoid adding new consequences in the middle of the conflict.");
        avoid.push("Avoid asking a question when you are really giving an instruction.");
      }

      return avoid.slice(0, 3);
    },

    buildWhy(ctx, scores, insight) {
      const childName = ctx.child && ctx.child.name ? ctx.child.name : "your child";

      const parts = [
        insight.summary
      ];

      if (scores.child.transitionNeed >= 3) {
        parts.push(`${childName} may need more support moving from one activity to another.`);
      }

      if (scores.child.autonomyNeed >= 3) {
        parts.push(`Giving limited choices may reduce the feeling of being controlled.`);
      }

      if (scores.parent.repetition >= 3) {
        parts.push(`Because you may naturally repeat yourself, this plan keeps the wording short and the follow-through clearer.`);
      }

      if (scores.parent.explanation >= 3) {
        parts.push(`Because you may naturally explain more, this plan limits debating in the difficult moment.`);
      }

      if (scores.family.parentLoad >= 3) {
        parts.push(`The plan is intentionally simple because you may be carrying a lot of the parenting load.`);
      }

      return parts.join(" ");
    },

    buildContributionIdea(ctx, scores) {
      const style = scores.child.contributionStyle;

      const ideas = {
        creative: {
          title: "Contribution idea",
          text: "Give this child a creative role this week: design a family game, decorate a chart, or plan a small family activity."
        },
        builder: {
          title: "Contribution idea",
          text: "Give this child a practical problem-solving role: organize cables, check batteries, build something simple, or help fix a household item safely."
        },
        helper: {
          title: "Contribution idea",
          text: "Give this child a helping role: prepare snacks, help a younger sibling, or be responsible for one small family kindness."
        },
        organizer: {
          title: "Contribution idea",
          text: "Give this child an organizing role: set up the bedtime checklist, arrange school supplies, or manage a simple routine chart."
        },
        general: {
          title: "Contribution idea",
          text: "Give this child one meaningful family job this week so they feel useful, not just corrected."
        }
      };

      return ideas[style] || ideas.general;
    },

    // ==========================================================================
    // 09. HELPERS
    // ==========================================================================

    labelSituation(value) {
      const map = {
        "bedtime": "Bedtime",
        "not-listening": "Not Listening",
        "screens": "Screen Time",
        "homework": "Homework",
        "morning": "Morning Routine",
        "siblings": "Sibling Conflict",
        "arguing": "Arguing",
        "meltdown": "Meltdown",
        "general": "Parenting Moment"
      };

      return map[value] || String(value || "Parenting Moment")
        .replace(/[-_]/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },

    uid(prefix = "engine") {
      return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    }
  };

})(window);
